import { useEffect, useMemo, useState } from "react";

import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { NumerolojiDietEditor, type DietDraft } from "@/components/admin/NumerolojiDietEditor";
import { Button } from "@/components/ui/button";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

import { Bell } from "lucide-react";

type DietProgramActivity = {
  activity_id: string;
  title: string;
  description: string;
  image_url: string;
  days: number[];
  time_of_day: string;
};

type CatalogDiet = {
  id: string;
  title: string;
  description: string;
  coverImageUrl?: string;
  waterTargetLiters?: number;
  mealType?: string;
  active: boolean;
  isPremium?: boolean;
  tags: string[];
  programActivities?: DietProgramActivity[];
  notes?: string;
};

type CatalogResponse = {
  ok: true;
  packages: unknown[];
  products: unknown[];
  meditations: unknown[];
  diets: CatalogDiet[];
  templates: unknown[];
  settings: unknown;
};

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await adminFetch("/api/admin/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function saveDiet(payload: Partial<CatalogDiet> & { title: string }) {
  const res = await adminFetch("/api/admin/numeroloji/diets", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Diyet kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogDiet };
}

const PROGRAM_DAYS_TAG_PREFIX = "program_gun:";

function parseProgramDaysFromTags(tags: string[]) {
  const raw = (tags ?? [])
    .find((tag) => String(tag ?? "").startsWith(PROGRAM_DAYS_TAG_PREFIX))
    ?.slice(PROGRAM_DAYS_TAG_PREFIX.length) ?? "";
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? String(Math.round(parsed)) : "";
}

function stripProgramDaysTag(tags: string[]) {
  return (tags ?? []).filter((tag) => !String(tag ?? "").startsWith(PROGRAM_DAYS_TAG_PREFIX));
}

function buildProgramDaysTag(programDays?: string | number) {
  const parsed = Number(programDays);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return `${PROGRAM_DAYS_TAG_PREFIX}${Math.round(parsed)}`;
}

const XP_TAG_PREFIX = "xp:";

function parseXpFromTags(tags: string[]) {
  const raw = (tags ?? [])
    .find((tag) => String(tag ?? "").match(/^xp:\s*\d{1,4}$/i))
    ?.slice(XP_TAG_PREFIX.length) ?? "";
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? String(Math.round(parsed)) : "";
}

function stripXpTag(tags: string[]) {
  return (tags ?? []).filter((tag) => !/^xp:\s*\d{1,4}$/i.test(String(tag ?? "").trim()));
}

function buildXpTag(xpPoints?: string | number) {
  const parsed = Number(xpPoints);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return `${XP_TAG_PREFIX}${Math.round(parsed)}`;
}

function emptyDraft(): DietDraft {
  return {
    title: "",
    description: "",
    coverImageUrl: "",
    waterTargetLiters: "",
    mealType: "",
    programDays: "",
    xpPoints: "",
    active: true,
    isPremium: false,
    tagsText: "genel",
    programActivities: [],
    notes: "",
    price: "0",
    store_product_id: null,
    kategori_id: undefined,
    title_en: "",
    title_es: "",
    description_en: "",
    description_es: "",
  };
}

export default function AdminNumerolojiDiet() {
  const params = useParams();
  const dietId = params.id ? String(params.id).trim() : "";
  const isNew = !dietId;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const catalogQuery = useQuery({
    queryKey: ["admin-numeroloji-catalog"],
    queryFn: fetchCatalog,
    enabled: authenticated,
    staleTime: 30_000,
  });

  const diets = useMemo(() => catalogQuery.data?.diets ?? [], [catalogQuery.data]);

  const [draft, setDraft] = useState<DietDraft>(() => emptyDraft());
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (!authenticated) return;

    if (isNew) {
      if (!seeded) {
        setDraft(emptyDraft());
        setSeeded(true);
      }
      return;
    }

    if (!catalogQuery.data || seeded) return;

    const item = diets.find((d) => d.id === dietId);
    if (!item) {
      setSeeded(true);
      return;
    }

    const programDays = parseProgramDaysFromTags(item.tags ?? []);
    const xpPoints = parseXpFromTags(item.tags ?? []);
    const visibleTags = stripXpTag(stripProgramDaysTag(item.tags ?? []));

    setDraft({
      id: item.id,
      title: item.title,
      description: item.description,
      coverImageUrl: item.coverImageUrl ?? "",
      waterTargetLiters: item.waterTargetLiters === undefined ? "" : String(item.waterTargetLiters),
      mealType: item.mealType ?? "",
      programDays,
      xpPoints,
      active: item.active,
      isPremium: item.isPremium ?? false,
      tagsText: visibleTags.join(", "),
      programActivities: item.programActivities ?? [],
      notes: item.notes ?? "",
      price: String((item as any).price ?? 0),
      store_product_id: (item as any).store_product_id ?? null,
      kategori_id: (item as any).kategori_id ?? undefined,
      title_en: (item as any).title_en ?? "",
      title_es: (item as any).title_es ?? "",
      description_en: (item as any).description_en ?? "",
      description_es: (item as any).description_es ?? "",
    });
    setSeeded(true);
  }, [authenticated, catalogQuery.data, dietId, diets, isNew, seeded]);

  const mutation = useMutation({
    mutationFn: (payload: any) => {
      const baseTags = stripXpTag(stripProgramDaysTag(Array.isArray(payload?.tags) ? payload.tags : []));
      const programDaysTag = buildProgramDaysTag(payload?.programDays);
      const xpTag = buildXpTag(payload?.xpPoints);
      const finalTags = [
        ...baseTags,
        ...(programDaysTag ? [programDaysTag] : []),
        ...(xpTag ? [xpTag] : []),
      ];
      return saveDiet({ ...payload, tags: finalTags, programActivities: payload?.programActivities ?? [] });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
      navigate("/admin/numeroloji/diyet", { replace: true });
    },
  });

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  if (!isNew && catalogQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!isNew && catalogQuery.isError) {
    return <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>;
  }

  if (!isNew && catalogQuery.data && !diets.some((d) => d.id === dietId)) {
    return (
      <div className="p-4 sm:p-6">
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          Diyet bulunamadı.
        </div>
        <div className="mt-4">
          <Button className="rounded-full" onClick={() => navigate("/admin/numeroloji/diyet", { replace: true })}>
            Diyetlere dön
          </Button>
        </div>
      </div>
    );
  }

  const headerTitle = isNew ? "Yeni Diyet" : "Diyet Düzenle";

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                {headerTitle}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <NumerolojiDietEditor
            draft={draft}
            setDraft={setDraft}
            isSaving={mutation.isPending}
            onClose={() => navigate("/admin/numeroloji/diyet", { replace: true })}
            onSave={(payload) => mutation.mutate(payload)}
          />

          {mutation.isError ? (
            <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {(mutation.error as Error).message}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
